import type { ReactNode } from 'react';
import { Col, Collapse, Row } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

export interface RuleItem {
  /** 规则标题。例：通关式打分，不允许越级。 */
  title: string;
  /** 规则正文。一段话，可包含中文长文本。 */
  desc: string;
  /** 主色 hex（用于序号气泡 / 左边框 / 第 N 条文案标记）。例：#1677ff。 */
  color: string;
  /** 主图标。单色 antd 图标，颜色由父级 style 注入，无需自带 color。 */
  icon: ReactNode;
}

interface Props {
  rules: RuleItem[];
  /** Collapse header 文本。默认「评价须知 · 请先阅读再开始打分」。 */
  title?: string;
}

/**
 * 评价规则展示面板。可折叠 + 卡片网格。
 *
 * 视觉规范：
 *  - 整体外层 Collapse 圆角 12，无 border，落到 #f7f9fc 浅底
 *  - 每条规则一张白底卡片：左侧 4px 主色边框 / 主色 16% 透明背景的圆形图标气泡 / 主色"第 N 条"小字 / 黑标题 / 灰描述
 *  - 字号节奏：标题 15px·600；描述 13px·1.75 行高；序号 11px·600 letter-spacing 1
 *  - hover 不需要交互动画（评价规则是阅读，不点）
 *
 * 在 PC 的 MyEvalTasksPage（素质项）和 LeaderCompetencyPage（胜任力）共用。
 */
export function RulesPanel({ rules, title = '评价须知 · 请先阅读再开始打分' }: Props) {
  return (
    <Collapse
      defaultActiveKey={['rules']}
      bordered={false}
      expandIconPosition="end"
      style={{
        marginBottom: 16,
        background: '#f7f9fc',
        borderRadius: 12,
        overflow: 'hidden',
      }}
      items={[
        {
          key: 'rules',
          label: (
            <span style={{ fontWeight: 600, fontSize: 14, color: '#1f1f1f' }}>
              <InfoCircleOutlined style={{ color: '#1677ff', marginRight: 8 }} />
              {title}
            </span>
          ),
          children: (
            <Row gutter={[16, 16]}>
              {rules.map((r, i) => (
                <Col key={i} xs={24} md={12}>
                  <RuleCard rule={r} index={i} />
                </Col>
              ))}
            </Row>
          ),
        },
      ]}
    />
  );
}

function RuleCard({ rule, index }: { rule: RuleItem; index: number }) {
  const ordinals = ['第一条', '第二条', '第三条', '第四条', '第五条', '第六条'];
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 10,
        border: '1px solid #f0f0f0',
        borderLeft: `4px solid ${rule.color}`,
        padding: '14px 16px',
        height: '100%',
        boxShadow: '0 2px 8px rgba(15,23,42,0.05)',
        display: 'flex',
        gap: 14,
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: `${rule.color}1F`, // ≈ 12% alpha
          color: rule.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
        }}
      >
        {rule.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: rule.color,
            letterSpacing: 1,
            marginBottom: 2,
          }}
        >
          {ordinals[index] || `第 ${index + 1} 条`}
        </div>
        <div
          style={{
            fontWeight: 600,
            fontSize: 15,
            color: '#1f1f1f',
            marginBottom: 6,
            lineHeight: 1.4,
          }}
        >
          {rule.title}
        </div>
        <div style={{ color: '#595959', fontSize: 13, lineHeight: 1.75 }}>{rule.desc}</div>
      </div>
    </div>
  );
}
